import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ChevronRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import MultiSelect from '../../components/MultiSelect';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { config } from '../../config/config';

interface DeclinedMoveData {
  pronumber: string;
  origin: string;
  destination: string;
  unitnumber: string;
  isMule?: boolean;
  rejectdatetime: string | null;
  rejectreason: string;
  rejectcomment: string;
}

interface DispatcherGroup {
  dispatcher: string;
  dispatcher_username: string;
  region: string;
  moves: DeclinedMoveData[];
}

interface DriverDeclinedMovesProps {
  selectedDispatchers: string[];
  selectedRegions: string[];
  selectedDate: Date | null;
  refreshTrigger: number;
  groupByDispatcher: boolean;
  usernameToDisplayname: Map<string, string>;
}

type SortColumn = 'pronumber' | 'unitnumber' | 'origin' | 'destination' | 'rejectreason' | 'rejectcomment' | 'rejectdatetime';
type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (datetime: string | null): string => {
  if (!datetime) return '-';
  const d = new Date(datetime);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const FILTER_INPUT_CLASS = 'w-full px-2 py-1 text-xs border border-slate-400 rounded-lg bg-white dark:bg-gray-900 text-heading placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600';

const DriverDeclinedMoves: React.FC<DriverDeclinedMovesProps> = ({
  selectedDispatchers,
  selectedRegions,
  selectedDate,
  refreshTrigger,
  groupByDispatcher,
  usernameToDisplayname,
}) => {
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [loading, setLoading] = useState(true);
  // Ref, NOT state: as state (and a dep of the fetch effect) the flag's flip
  // after the first load re-ran the effect, so every page load fetched twice.
  const initialLoadCompleteRef = useRef(false);
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: null });
  const [columnSearches, setColumnSearches] = useState({
    pronumber: '',
    unitnumber: '',
    origin: '',
    destination: '',
    rejectcomment: '',
    rejectdatetime: '',
  });
  const [reasonFilter, setReasonFilter] = useState<string[]>([]);

  // Order details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const legacyCmsUrl = config.LEGACY_CMS_URL;

  const openOrderDrawer = useCallback((pronumber: number | string) => {
    const proNum = typeof pronumber === 'string' ? parseInt(pronumber, 10) : pronumber;
    setSelectedPro(proNum);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDate) return;

      const isBackgroundRefresh = initialLoadCompleteRef.current;

      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      try {
        const dateStr = formatDateKey(selectedDate);
        const response = await api.get('/driver-dispatch/declined-moves', {
          params: { date: dateStr },
        });
        setData(response.data || []);
        // Auto-expand all dispatchers
        setExpandedDispatchers(new Set((response.data || []).map((g: DispatcherGroup) => g.dispatcher)));
        initialLoadCompleteRef.current = true;
      } catch (error) {
        console.error('Error fetching declined moves:', error);
        if (!isBackgroundRefresh) {
          setData([]);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [selectedDate, refreshTrigger]);

  // Derive unique reason options from the data
  const reasonOptions = useMemo(() => {
    const reasons = new Set<string>();
    data.forEach(group => group.moves.forEach(move => {
      if (move.rejectreason) reasons.add(move.rejectreason);
    }));
    return Array.from(reasons).sort().map(r => ({ value: r, label: r }));
  }, [data]);

  // Filter data by selected dispatchers, regions, reason filter, and column searches
  const filteredData = useMemo(() => {
    const hasColumnSearch = Object.values(columnSearches).some(v => v);
    const hasReasonFilter = reasonFilter.length > 0;
    const hasAnyFilter = hasColumnSearch || hasReasonFilter;

    return data
      .filter(group => {
        if (selectedDispatchers.length > 0) {
          const selectedDisplaynames = new Set(
            selectedDispatchers.map(username => usernameToDisplayname.get(username) || username)
          );
          if (!selectedDisplaynames.has(group.dispatcher)) return false;
        }
        if (selectedRegions.length > 0 && !selectedRegions.includes(group.region)) {
          return false;
        }
        return true;
      })
      .map(group => {
        if (!hasAnyFilter) return group;

        const filtered = group.moves.filter(move => {
          if (columnSearches.pronumber && !String(move.pronumber).toLowerCase().includes(columnSearches.pronumber.toLowerCase())) return false;
          if (columnSearches.unitnumber && !String(move.unitnumber).toLowerCase().includes(columnSearches.unitnumber.toLowerCase())) return false;
          if (columnSearches.origin && !String(move.origin || '').toLowerCase().includes(columnSearches.origin.toLowerCase())) return false;
          if (columnSearches.destination && !String(move.destination || '').toLowerCase().includes(columnSearches.destination.toLowerCase())) return false;
          if (hasReasonFilter && !reasonFilter.includes(move.rejectreason)) return false;
          if (columnSearches.rejectcomment && !String(move.rejectcomment || '').toLowerCase().includes(columnSearches.rejectcomment.toLowerCase())) return false;
          if (columnSearches.rejectdatetime && !formatDateTime(move.rejectdatetime).toLowerCase().includes(columnSearches.rejectdatetime.toLowerCase())) return false;
          return true;
        });

        return { ...group, moves: filtered };
      })
      .filter(group => group.moves.length > 0);
  }, [data, selectedDispatchers, selectedRegions, usernameToDisplayname, columnSearches, reasonFilter]);

  // Sort moves
  const sortMoves = (moves: DeclinedMoveData[]): DeclinedMoveData[] => {
    if (!sortState.column || !sortState.direction) return moves;

    return [...moves].sort((a, b) => {
      const col = sortState.column!;
      const dir = sortState.direction === 'asc' ? 1 : -1;
      const aVal = (a[col] || '').toString().toLowerCase();
      const bVal = (b[col] || '').toString().toLowerCase();
      return aVal.localeCompare(bVal) * dir;
    });
  };

  // Flat list for ungrouped view
  const flatMoveList = useMemo(() => {
    if (groupByDispatcher) return [];
    const all = filteredData.flatMap(group =>
      group.moves.map(move => ({
        ...move,
        dispatcher: group.dispatcher,
        dispatcher_region: group.region,
      }))
    );
    return sortMoves(all);
  }, [filteredData, groupByDispatcher, sortState]);

  // Total count
  const totalCount = useMemo(() => {
    return filteredData.reduce((sum, group) => sum + group.moves.length, 0);
  }, [filteredData]);

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const next = new Set(prev);
      if (next.has(dispatcher)) {
        next.delete(dispatcher);
      } else {
        next.add(dispatcher);
      }
      return next;
    });
  };

  const handleSort = (column: SortColumn) => {
    setSortState(prev => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      return { column: null, direction: null };
    });
  };

  const SortableHeader: React.FC<{ column: SortColumn; label: string; className?: string }> = ({ column, label, className = '' }) => (
    <th
      className={`p-3 text-left font-semibold border-r border-slate-500 cursor-pointer hover:bg-slate-500 transition-colors whitespace-nowrap ${className}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="text-slate-400">
          {sortState.column === column ? (
            sortState.direction === 'asc' ? '↑' : '↓'
          ) : (
            <span className="opacity-50">↕</span>
          )}
        </span>
      </div>
    </th>
  );

  const COL_COUNT = 7;

  const renderMoveRow = (move: DeclinedMoveData, idx: number) => (
    <tr
      key={`${move.pronumber}-${move.unitnumber}-${idx}`}
      className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 ${
        idx % 2 === 0
          ? 'bg-panel'
          : 'bg-gray-50 dark:bg-gray-800/50'
      }`}
    >
      <td className="p-3 pl-8 text-sm border-r border-gray-200 dark:border-gray-700 font-medium">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openOrderDrawer(move.pronumber);
            }}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
          >
            {move.pronumber}
          </button>
          <a
            href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${move.pronumber}&searchtype=pronumber`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            title="Open in CMSV1"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      </td>
      <td className="p-3 text-sm border-r border-gray-200 dark:border-gray-700">{move.unitnumber}<MuleBadge isMule={move.isMule} /></td>
      <td className="p-3 text-sm border-r border-gray-200 dark:border-gray-700">{move.origin}</td>
      <td className="p-3 text-sm border-r border-gray-200 dark:border-gray-700">{move.destination}</td>
      <td className="p-3 text-sm border-r border-gray-200 dark:border-gray-700">{move.rejectreason}</td>
      <td className="p-3 text-sm border-r border-gray-200 dark:border-gray-700">{move.rejectcomment || '-'}</td>
      <td className="p-3 text-sm">{formatDateTime(move.rejectdatetime)}</td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-muted">Loading declined moves...</div>
      </div>
    );
  }

  if (totalCount === 0 && !Object.values(columnSearches).some(v => v)) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-muted">
          No declined moves for {selectedDate ? formatDateKey(selectedDate) : 'selected date'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
      <table className="w-full border-collapse">
        <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-10">
          <tr>
            <SortableHeader column="pronumber" label="Pro #" className="w-40" />
            <SortableHeader column="unitnumber" label="Driver" className="w-28" />
            <SortableHeader column="origin" label="Origin" />
            <SortableHeader column="destination" label="Destination" />
            <SortableHeader column="rejectreason" label="Reason" className="w-48" />
            <SortableHeader column="rejectcomment" label="Comment" />
            <SortableHeader column="rejectdatetime" label="Time" className="w-28" />
          </tr>
          {/* Filter Row */}
          <tr className="bg-slate-500">
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.pronumber}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, pronumber: e.target.value }))}
                placeholder="Filter Pro #"
                className={FILTER_INPUT_CLASS}
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.unitnumber}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, unitnumber: e.target.value }))}
                placeholder="Filter driver"
                className={FILTER_INPUT_CLASS}
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.origin}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, origin: e.target.value }))}
                placeholder="Filter origin"
                className={FILTER_INPUT_CLASS}
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.destination}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, destination: e.target.value }))}
                placeholder="Filter destination"
                className={FILTER_INPUT_CLASS}
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <MultiSelect
                options={reasonOptions}
                value={reasonFilter}
                onChange={setReasonFilter}
                placeholder="All"
                showClearOption={true}
                clearOptionLabel="All"
                enableSearch={false}
                size="sm"
                className="w-full"
              />
            </th>
            <th className="p-1 border-r border-slate-400">
              <input
                type="text"
                value={columnSearches.rejectcomment}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, rejectcomment: e.target.value }))}
                placeholder="Filter comment"
                className={FILTER_INPUT_CLASS}
              />
            </th>
            <th className="p-1">
              <input
                type="text"
                value={columnSearches.rejectdatetime}
                onChange={(e) => setColumnSearches(prev => ({ ...prev, rejectdatetime: e.target.value }))}
                placeholder="Filter time"
                className={FILTER_INPUT_CLASS}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {groupByDispatcher ? (
            filteredData.map((group) => (
              <React.Fragment key={group.dispatcher}>
                {/* Dispatcher header row */}
                <tr
                  className="bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  onClick={() => toggleDispatcher(group.dispatcher)}
                >
                  <td colSpan={COL_COUNT} className="p-2">
                    <div className="flex items-center gap-2">
                      <ChevronRightIcon
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          expandedDispatchers.has(group.dispatcher) ? 'rotate-90' : ''
                        }`}
                      />
                      <span className="font-medium text-heading">
                        {group.dispatcher}
                      </span>
                      <span className="text-sm text-muted">
                        ({group.moves.length} decline{group.moves.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </td>
                </tr>
                {/* Move rows */}
                {expandedDispatchers.has(group.dispatcher) &&
                  sortMoves(group.moves).map((move, idx) => renderMoveRow(move, idx))}
              </React.Fragment>
            ))
          ) : (
            flatMoveList.map((move, idx) => renderMoveRow(move, idx))
          )}
        </tbody>
      </table>
      {/* Footer with count */}
      <div className="sticky bottom-0 bg-gray-100 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
        Total: {totalCount} declined move{totalCount !== 1 ? 's' : ''}
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
};

export default DriverDeclinedMoves;
